import React, { useState, useEffect, useCallback, useRef } from 'react';
import toast from 'react-hot-toast';
import { customizationsApi } from '../../api/customizations.js';
import { productsApi } from '../../api/products.js';



const INPUT_TYPES = [
  { value: 'text',     label: 'Text Field' },
  { value: 'number',   label: 'Number Field' },
  { value: 'select',   label: 'Select Dropdown' },
  { value: 'media',    label: 'Media Upload' },
  { value: 'color',    label: 'Color Picker' },
  { value: 'textarea', label: 'Paragraph/Textarea' },
];

function useDebounce(value, delay = 400) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

/* ══════════════════════════════════════════
   ADD / EDIT MODAL
══════════════════════════════════════════ */
const AddEditModal = ({ customization, onClose, onSuccess }) => {
  const isEdit = !!customization;

  const [form, setForm] = useState({
    label: customization?.label || '',
    input_type: customization?.input_type || 'text',
    placeholder: customization?.placeholder || '',
    min_value: customization?.min_value != null ? customization.min_value : '',
    max_value: customization?.max_value != null ? customization.max_value : '',
    unit: customization?.unit || '',
    options_string: customization?.options ? customization.options.join(', ') : '',
    is_required: customization?.is_required === true,
    is_active: customization?.is_active !== false,
  });

  const [mediaFile, setMediaFile] = useState(null);
  const [mediaPreview, setMediaPreview] = useState(customization?.media_url || null);
  const [removeMedia, setRemoveMedia] = useState(false);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const [allProducts, setAllProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [productSearch, setProductSearch] = useState('');
  const [assignedProductIds, setAssignedProductIds] = useState([]);
  const [selectedProductIds, setSelectedProductIds] = useState([]);
  const [productAssignmentMap, setProductAssignmentMap] = useState({});

  useEffect(() => {
    setLoadingProducts(true);
    productsApi.getAll({ limit: 500, sortBy: 'name', sortOrder: 'ASC' })
      .then(res => setAllProducts(res?.data || []))
      .catch(() => {})
      .finally(() => setLoadingProducts(false));
  }, []);

  useEffect(() => {
    if (isEdit && customization?.id) {
      customizationsApi.getById(customization.id).then(res => {
        if (res?.success) {
          const assignments = res.data?.assignments || [];
          const ids = assignments.map(a => a.product_id);
          setAssignedProductIds(ids);
          setSelectedProductIds(ids);
          const map = {};
          assignments.forEach(a => { map[a.product_id] = a.id; });
          setProductAssignmentMap(map);
        }
      }).catch(() => {});
    }
  }, [isEdit, customization]);

  const set = (k, v) => { setForm(f => ({ ...f, [k]: v })); setErrors(e => ({ ...e, [k]: '' })); };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('File size exceeds 5 MB limit.'); return; }
    setMediaFile(file);
    setMediaPreview(URL.createObjectURL(file));
    setRemoveMedia(false);
  };

  const validate = () => {
    const e = {};
    if (!form.label.trim()) e.label = 'Label is required.';
    if (form.input_type === 'number') {
      if (form.min_value !== '' && isNaN(Number(form.min_value))) e.min_value = 'Must be a valid number.';
      if (form.max_value !== '' && isNaN(Number(form.max_value))) e.max_value = 'Must be a valid number.';
      if (form.min_value !== '' && form.max_value !== '' && Number(form.min_value) > Number(form.max_value)) e.max_value = 'Max cannot be less than min.';
    }
    if (form.input_type === 'select' && !form.options_string.trim()) e.options_string = 'Provide comma-separated options.';
    return e;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('label', form.label.trim());
      fd.append('input_type', form.input_type);
      fd.append('placeholder', form.placeholder.trim());
      fd.append('is_required', form.is_required ? 'true' : 'false');
      if (form.input_type === 'number') {
        if (form.min_value !== '') fd.append('min_value', form.min_value);
        if (form.max_value !== '') fd.append('max_value', form.max_value);
        if (form.unit.trim()) fd.append('unit', form.unit.trim());
      }
      if (form.input_type === 'select' && form.options_string.trim()) {
        fd.append('options', JSON.stringify(form.options_string.split(',').map(s => s.trim()).filter(Boolean)));
      }
      if (mediaFile) fd.append('media', mediaFile);

      let custId;
      if (isEdit) {
        fd.append('is_active', form.is_active ? 'true' : 'false');
        if (removeMedia) fd.append('remove_media', 'true');
        await customizationsApi.update(customization.id, fd);
        custId = customization.id;
        toast.success('Customization updated!');
      } else {
        const res = await customizationsApi.create(fd);
        custId = res?.data?.id;
        toast.success('Customization created!');
      }

      if (custId) {
        if (isEdit) {
          const toAdd = selectedProductIds.filter(id => !assignedProductIds.includes(id));
          const toRemove = assignedProductIds.filter(id => !selectedProductIds.includes(id));
          await Promise.all([
            ...toAdd.map(pid => customizationsApi.assign(pid, { customization_id: custId })),
            ...toRemove.map(pid => {
              const aid = productAssignmentMap[pid];
              return aid ? customizationsApi.removeAssignment(pid, aid) : Promise.resolve();
            }),
          ]);
        } else {
          await Promise.all(selectedProductIds.map(pid => customizationsApi.assign(pid, { customization_id: custId })));
        }
      }

      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.error || `Failed to ${isEdit ? 'update' : 'create'} customization.`);
    } finally { setSaving(false); }
  };

  const filteredProducts = allProducts.filter(p =>
    (p.name || '').toLowerCase().includes(productSearch.toLowerCase()) ||
    (p.sku || '').toLowerCase().includes(productSearch.toLowerCase())
  );

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 680 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <span style={{ fontWeight: 700 }}>{isEdit ? 'Edit Customization' : 'Create Customization'}</span>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body" style={{ maxHeight: 540, overflowY: 'auto' }}>

            {/* Label */}
            <div className="form-group">
              <label className="form-label">Customization Label *</label>
              <input className={`form-control${errors.label ? ' is-invalid' : ''}`} value={form.label} onChange={e => set('label', e.target.value)} placeholder="e.g. Paper Color, Roll Width, Logo Upload" />
              {errors.label && <div style={{ color: '#dc2626', fontSize: 12, marginTop: 4 }}>{errors.label}</div>}
            </div>

            {/* Input Type */}
            <div className="form-group">
              <label className="form-label">Input Type</label>
              <select className="form-control" value={form.input_type} onChange={e => set('input_type', e.target.value)} disabled={isEdit}>
                {INPUT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>

            {/* Placeholder */}
            <div className="form-group">
              <label className="form-label">Placeholder / Help Text</label>
              <input className="form-control" value={form.placeholder} onChange={e => set('placeholder', e.target.value)} placeholder="Hint shown to sales executives" />
            </div>

            {/* Number fields */}
            {form.input_type === 'number' && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: 12, marginBottom: 16 }}>
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label" style={{ fontSize: '0.75rem' }}>Min Value</label>
                  <input type="number" className={`form-control${errors.min_value ? ' is-invalid' : ''}`} value={form.min_value} onChange={e => set('min_value', e.target.value)} placeholder="None" />
                  {errors.min_value && <div style={{ color: '#dc2626', fontSize: 11, marginTop: 2 }}>{errors.min_value}</div>}
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label" style={{ fontSize: '0.75rem' }}>Max Value</label>
                  <input type="number" className={`form-control${errors.max_value ? ' is-invalid' : ''}`} value={form.max_value} onChange={e => set('max_value', e.target.value)} placeholder="None" />
                  {errors.max_value && <div style={{ color: '#dc2626', fontSize: 11, marginTop: 2 }}>{errors.max_value}</div>}
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label" style={{ fontSize: '0.75rem' }}>Unit</label>
                  <input className="form-control" value={form.unit} onChange={e => set('unit', e.target.value)} placeholder="e.g. mm, cm" />
                </div>
              </div>
            )}

            {/* Select options */}
            {form.input_type === 'select' && (
              <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: 12, marginBottom: 16 }}>
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label">Allowed Options *</label>
                  <input className={`form-control${errors.options_string ? ' is-invalid' : ''}`} value={form.options_string} onChange={e => set('options_string', e.target.value)} placeholder="e.g. White, Yellow, Pink, Blue" />
                  <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>Separate options with commas</div>
                  {errors.options_string && <div style={{ color: '#dc2626', fontSize: 12, marginTop: 4 }}>{errors.options_string}</div>}
                </div>
              </div>
            )}

            {/* Media */}
            <div className="form-group">
              <label className="form-label">Reference Image / Logo (Optional)</label>
              {mediaPreview && !removeMedia ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, background: '#f8fafc', padding: 10, borderRadius: 8, border: '1px solid #e2e8f0' }}>
                  <img src={mediaPreview} alt="preview" style={{ width: 48, height: 48, objectFit: 'contain', borderRadius: 4, border: '1px solid #e2e8f0' }} />
                  <div style={{ flex: 1 }}>
                    <span style={{ fontSize: '0.8rem', fontWeight: 600, display: 'block' }}>{mediaFile ? mediaFile.name : 'Saved preview'}</span>
                    <button type="button" style={{ fontSize: '0.75rem', color: '#dc2626', background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontWeight: 600 }} onClick={() => { isEdit ? setRemoveMedia(true) : (setMediaFile(null), setMediaPreview(null)); }}>
                      Remove image
                    </button>
                  </div>
                </div>
              ) : (
                <div style={{ border: '2px dashed #cbd5e1', borderRadius: 8, padding: 16, textAlign: 'center', cursor: 'pointer', background: '#fafafa' }} onClick={() => fileRef.current?.click()}>
                  <div style={{ fontSize: 24, marginBottom: 4 }}>📷</div>
                  <p style={{ fontWeight: 600, margin: '0 0 2px', fontSize: 13 }}>Click to upload reference image</p>
                  <p style={{ fontSize: 11, color: '#94a3b8', margin: 0 }}>PNG, JPG, WEBP, SVG · max 5 MB</p>
                </div>
              )}
              <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleFileChange} />
            </div>

            {/* Checkboxes */}
            <div style={{ display: 'flex', gap: 24, marginTop: 8 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.85rem', cursor: 'pointer' }}>
                <input type="checkbox" checked={form.is_required} onChange={e => set('is_required', e.target.checked)} />
                is required on order
              </label>
              {isEdit && (
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.85rem', cursor: 'pointer' }}>
                  <input type="checkbox" checked={form.is_active} onChange={e => set('is_active', e.target.checked)} />
                  Customization Active
                </label>
              )}
            </div>

            {/* Direct Product Assignment */}
            <div style={{ marginTop: 20, borderTop: '1.5px solid #e2e8f0', paddingTop: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <label className="form-label" style={{ margin: 0, fontWeight: 700 }}>
                  Direct Product Assignment <span style={{ fontWeight: 400, color: '#94a3b8', fontSize: '0.72rem' }}>(Optional)</span>
                </label>
                {selectedProductIds.length > 0 && (
                  <span style={{ background: '#eef2ff', color: '#6366f1', fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 99, border: '1px solid #c7d2fe' }}>
                    ✓ {selectedProductIds.length} Assigned
                  </span>
                )}
              </div>

              <div style={{ position: 'relative', marginBottom: 8 }}>
                <input className="form-control" style={{ fontSize: '0.8rem' }} value={productSearch} onChange={e => setProductSearch(e.target.value)} placeholder="Search products by name or SKU…" />
                {productSearch && <button type="button" onClick={() => setProductSearch('')} style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer' }}>✕</button>}
              </div>

              {loadingProducts ? (
                <div style={{ textAlign: 'center', padding: 16, color: '#94a3b8', fontSize: 13 }}>Loading products…</div>
              ) : allProducts.length === 0 ? (
                <div style={{ fontSize: '0.8rem', color: '#94a3b8', padding: '8px 0' }}>No products in catalog.</div>
              ) : (
                <div style={{ maxHeight: 150, overflowY: 'auto', border: '1.5px solid #e2e8f0', borderRadius: 8, background: '#fff' }}>
                  {filteredProducts.map(p => {
                    const isSel = selectedProductIds.includes(p.id);
                    return (
                      <div key={p.id} onClick={() => setSelectedProductIds(ids => isSel ? ids.filter(id => id !== p.id) : [...ids, p.id])}
                        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '7px 12px', borderBottom: '1px solid #f1f5f9', cursor: 'pointer', background: isSel ? '#eef2ff' : 'transparent' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <input type="checkbox" checked={isSel} onChange={() => {}} style={{ width: 14, height: 14, accentColor: '#6366f1' }} />
                          <span style={{ fontSize: '0.82rem', fontWeight: 600, color: isSel ? '#6366f1' : '#1e293b' }}>{p.name}</span>
                          {p.sku && <span style={{ fontFamily: 'monospace', fontSize: '0.68rem', color: '#94a3b8', background: '#f1f5f9', padding: '1px 4px', borderRadius: 3, border: '1px solid #e2e8f0' }}>{p.sku}</span>}
                        </div>
                        <span style={{ fontSize: '0.8rem', fontWeight: 700, color: '#64748b' }}>₹{p.min_price?.toLocaleString('en-IN')}</span>
                      </div>
                    );
                  })}
                  {filteredProducts.length === 0 && <div style={{ padding: 12, textAlign: 'center', fontSize: '0.8rem', color: '#94a3b8' }}>No matching products.</div>}
                </div>
              )}
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-outline btn-sm" onClick={onClose} disabled={saving}>Cancel</button>
            <button type="submit" className="btn btn-primary btn-sm" disabled={saving}>{saving ? 'Saving…' : (isEdit ? 'Save Changes' : 'Create')}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

/* ══════════════════════════════════════════
   MAIN PAGE
══════════════════════════════════════════ */
export default function ProductCustomizationsPage() {
  const [customizations, setCustomizations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [inputSearch, setInputSearch] = useState('');
  const debouncedSearch = useDebounce(inputSearch, 400);
  const [activeFilter, setActiveFilter] = useState('true');
  const [showModal, setShowModal] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [limit, setLimit] = useState(20);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await customizationsApi.getAll({ page, limit: limit === 100000 ? 100000 : limit, search: debouncedSearch || undefined, active: activeFilter });
      if (res?.success) {
        setCustomizations(res.data || []);
        setTotalCount(res.count || 0);
        setTotalPages(res.totalPages || 1);
      }
    } catch { toast.error('Failed to load customizations.'); }
    finally { setLoading(false); }
  }, [page, debouncedSearch, activeFilter, limit]);

  useEffect(() => { fetchList(); }, [fetchList]);
  useEffect(() => { setPage(1); }, [debouncedSearch]);

  const handleDelete = async (item) => {
    if (!window.confirm(`Delete "${item.label}"? It will be unlinked from ALL products. This cannot be undone!`)) return;
    try {
      await customizationsApi.delete(item.id);
      toast.success('Customization deleted!');
      fetchList();
    } catch { toast.error('Failed to delete.'); }
  };

  const getTypeName = (val) => INPUT_TYPES.find(t => t.value === val)?.label || val;

  return (
    <div className="page-body">
      <div className="card">
        <div className="card-header">
          <div>
            <span className="card-title">Product Customizations</span>
            <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: 13 }}>{totalCount} standalone specifications · assigned dynamically to products</p>
          </div>
          <button className="btn btn-primary btn-sm" onClick={() => { setEditTarget(null); setShowModal(true); }}>+ Add Customization</button>
        </div>

        <div style={{ padding: '12px 20px', borderBottom: '1px solid #e2e8f0', display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{ position: 'relative' }}>
            <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', pointerEvents: 'none' }}>🔍</span>
            <input style={{ paddingLeft: 32 }} className="form-control" value={inputSearch} onChange={e => setInputSearch(e.target.value)} placeholder="Search customizations by label…" />
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            {[{ label: 'Active Only', value: 'true' }, { label: 'Include Inactive', value: 'false' }].map(f => (
              <button key={f.value}
                onClick={() => { setActiveFilter(f.value); setPage(1); }}
                style={{ fontSize: 12, fontWeight: 600, padding: '5px 12px', borderRadius: 6, border: `1px solid ${activeFilter === f.value ? '#6366f1' : '#e2e8f0'}`, background: activeFilter === f.value ? '#eef2ff' : 'transparent', color: activeFilter === f.value ? '#6366f1' : '#64748b', cursor: 'pointer' }}>
                {f.label}
              </button>
            ))}
          </div>
        </div>

        <div className="card-body" style={{ padding: 0 }}>
          {loading && customizations.length === 0 ? (
            <div style={{ padding: 48, textAlign: 'center', color: '#94a3b8' }}>Loading…</div>
          ) : customizations.length === 0 ? (
            <div style={{ padding: '48px 0', textAlign: 'center', color: '#94a3b8' }}>
              <div style={{ fontSize: 36, marginBottom: 8 }}>🏷️</div>
              <p style={{ fontWeight: 600, margin: '0 0 4px' }}>No customizations found</p>
              <p style={{ fontSize: 13, margin: '0 0 12px' }}>Create a customization standard, then assign it to catalog products.</p>
              <button className="btn btn-primary btn-sm" onClick={() => { setEditTarget(null); setShowModal(true); }}>+ Add Customization</button>
            </div>
          ) : (
            <div className="table-wrapper" style={{ opacity: loading ? 0.6 : 1, transition: 'opacity .2s' }}>
              <table>
                <thead>
                  <tr>
                    <th style={{ width: 48 }}>ID</th>
                    <th style={{ width: 52 }}>Image</th>
                    <th>Label</th>
                    <th>Input Type</th>
                    <th>Required</th>
                    <th>Placeholder</th>
                    <th>Scope / Options</th>
                    <th>Status</th>
                    <th style={{ width: 80 }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {customizations.map(item => (
                    <tr key={item.id}>
                      <td style={{ color: '#94a3b8', fontSize: 12 }}>{item.id}</td>
                      <td>
                        {item.media_url
                          ? <img src={item.media_url} alt={item.label} style={{ width: 36, height: 36, objectFit: 'contain', borderRadius: 4, border: '1px solid #e2e8f0' }} />
                          : <div style={{ width: 36, height: 36, background: '#f1f5f9', color: '#94a3b8', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 4, border: '1px dashed #cbd5e1', fontSize: 16 }}>🖼</div>
                        }
                      </td>
                      <td style={{ fontWeight: 600 }}>{item.label}</td>
                      <td style={{ fontSize: 13 }}>{getTypeName(item.input_type)}</td>
                      <td>
                        <span className={`badge ${item.is_required ? 'badge-danger' : 'badge-success'}`}>{item.is_required ? 'Mandatory' : 'Optional'}</span>
                      </td>
                      <td style={{ color: '#64748b', fontSize: 13 }}>{item.placeholder || '—'}</td>
                      <td>
                        {item.input_type === 'select' && item.options ? (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                            {item.options.map(opt => <span key={opt} style={{ fontSize: 11, padding: '2px 6px', background: '#f1f5f9', borderRadius: 4, border: '1px solid #e2e8f0' }}>{opt}</span>)}
                          </div>
                        ) : item.input_type === 'number' && (item.min_value != null || item.max_value != null) ? (
                          <span style={{ fontFamily: 'monospace', fontSize: 12 }}>
                            {item.min_value != null ? `Min: ${item.min_value}` : ''}{item.min_value != null && item.max_value != null ? ' · ' : ''}{item.max_value != null ? `Max: ${item.max_value}` : ''}{item.unit ? ` (${item.unit})` : ''}
                          </span>
                        ) : <span style={{ color: '#cbd5e1' }}>—</span>}
                      </td>
                      <td>
                        <span className={`badge ${item.is_active ? 'badge-success' : 'badge-danger'}`}>{item.is_active ? 'Active' : 'Inactive'}</span>
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button className="btn btn-xs btn-outline" title="Edit" onClick={() => { setEditTarget(item); setShowModal(true); }}>✎</button>
                          <button className="btn btn-xs" title="Delete" style={{ background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca' }} onClick={() => handleDelete(item)}>🗑</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {totalPages > 1 && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16, padding: '16px 20px', borderTop: '1px solid #e2e8f0', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 13, color: '#475569', fontWeight: 500 }}>Show:</span>
              <select
                className="form-control"
                style={{ width: 'auto', padding: '4px 8px', fontSize: 13, cursor: 'pointer', height: 'auto' }}
                value={limit === 100000 ? 'all' : limit}
                onChange={e => { const v = e.target.value; setLimit(v === 'all' ? 100000 : parseInt(v, 10)); setPage(1); }}
              >
                <option value={20}>20</option>
                <option value={100}>100</option>
                <option value={200}>200</option>
                <option value={500}>500</option>
                <option value="all">All</option>
              </select>
            </div>
            <button className="btn btn-outline btn-sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>← Prev</button>
            <span style={{ fontSize: 13, color: '#64748b', fontWeight: 600 }}>Page {page} of {totalPages}</span>
            <button className="btn btn-outline btn-sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>Next →</button>
          </div>
        )}
      </div>

      {showModal && (
        <AddEditModal
          customization={editTarget}
          onClose={() => { setShowModal(false); setEditTarget(null); }}
          onSuccess={() => { setShowModal(false); setEditTarget(null); fetchList(); }}
        />
      )}
    </div>
  );
}
